import { useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRequestPasswordResetMutation } from '../api';
import { ResetPasswordForm } from '../components/ResetPasswordForm';
import { reportMutationError } from '@/lib/mutationError';
import { executeRecaptcha } from '@lib/recaptcha';

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation('auth');
  // When `:token` is present the user arrived from a reset-email link → show
  // the reset form; otherwise show the request-a-reset-link email form.
  const { token } = useParams<{ token?: string }>();
  const [submitted, setSubmitted] = useState(false);
  // Guards the whole submit flow, including the reCAPTCHA preflight that runs
  // before the mutation's `isLoading` flips — otherwise repeated clicks during
  // that window could queue multiple reset requests.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestReset, { isLoading }] = useRequestPasswordResetMutation();

  const schema = z.object({
    email: z
      .string()
      .min(1, t('signIn.errors.required'))
      .email(t('signIn.errors.invalidEmail')),
  });
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Attach a reCAPTCHA token; the server verifies it. `executeRecaptcha` resolves to null
      // when reCAPTCHA is disabled via VITE_CAPTCHA_ENABLED or no site key is configured.
      const captchaToken = (await executeRecaptcha('forgot_password')) ?? undefined;
      // Always show the success state — never reveal whether an account exists.
      await requestReset({ email, captchaToken }).unwrap().catch(reportMutationError);
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  });

  const title = token
    ? t('resetPassword.title')
    : submitted
      ? t('forgotPassword.success.title')
      : t('forgotPassword.title');
  const description = token
    ? t('resetPassword.subtitle')
    : submitted
      ? t('forgotPassword.success.body')
      : t('forgotPassword.subtitle');

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Card data-testid="forgot-password-page">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {token && <ResetPasswordForm token={token} />}
            {!token && !submitted && (
              <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="forgot-email" className="text-sm font-medium">
                    {t('signIn.username')}
                  </label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('signIn.usernamePlaceholder')}
                    aria-invalid={Boolean(errors.email)}
                    data-testid="forgot-password-email"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  )}
                </div>
                <Button type="submit" disabled={isLoading || isSubmitting} data-testid="forgot-password-submit">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('forgotPassword.submitting')}
                    </>
                  ) : (
                    t('forgotPassword.submit')
                  )}
                </Button>
              </form>
            )}

            <div className="text-center text-sm">
              <Link
                to="/signin"
                className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                data-testid="forgot-password-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('forgotPassword.backToSignIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
